import type { StoredMeal } from '@/types/mealPlan'

// Browser-durable backing for the meals freeze-cache. Only FROZEN meals are persisted — they're
// immutable, so a persisted copy never needs invalidation. Fails SOFT: a blocked or quota-exceeded
// IndexedDB degrades to no persistence rather than throwing into the app.

const DB_NAME = 'parsley-meal-cache'
// Bump on any breaking change to the persisted shape — onupgradeneeded recreates
// the store, discarding stale-shaped entries.
const DB_VERSION = 1
const STORE = 'frozenMeals'

// The persisted meal record: the canonical doc minus the unused timestamps.
export type PersistedMeal = Omit<StoredMeal, 'createdAt' | 'updatedAt'>

interface CacheRow {
  cacheKey: string // `${uid}:${mealId}`
  uid: string
  mealId: string
  doc: PersistedMeal
}

function openDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null)
  return new Promise((resolve) => {
    let req: IDBOpenDBRequest
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION)
    } catch {
      resolve(null)
      return
    }
    req.onupgradeneeded = () => {
      const dbi = req.result
      if (dbi.objectStoreNames.contains(STORE)) dbi.deleteObjectStore(STORE)
      const store = dbi.createObjectStore(STORE, { keyPath: 'cacheKey' })
      store.createIndex('uid', 'uid', { unique: false })
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => resolve(null)
    req.onblocked = () => resolve(null)
  })
}

// All persisted (frozen) meals for a user. Returns the raw records; the caller
// re-applies the image placeholder.
export async function idbLoadMeals(uid: string): Promise<PersistedMeal[]> {
  const dbi = await openDb()
  if (!dbi) return []
  return new Promise((resolve) => {
    try {
      const req = dbi.transaction(STORE, 'readonly').objectStore(STORE).index('uid').getAll(uid)
      req.onsuccess = () => resolve((req.result as CacheRow[]).map((r) => r.doc))
      req.onerror = () => resolve([])
    } catch {
      resolve([])
    } finally {
      dbi.close()
    }
  })
}

// Persist one frozen meal. Fire-and-forget — the write races the app harmlessly.
export async function idbPutMeal(uid: string, meal: StoredMeal): Promise<void> {
  const dbi = await openDb()
  if (!dbi) return
  try {
    const { createdAt: _createdAt, updatedAt: _updatedAt, ...doc } = meal
    dbi
      .transaction(STORE, 'readwrite')
      .objectStore(STORE)
      .put({ cacheKey: `${uid}:${meal.mealId}`, uid, mealId: meal.mealId, doc })
  } catch {
    // ignore — persistence is best-effort
  } finally {
    dbi.close()
  }
}

// Wipe a user's persisted meals (sign-out / the "clear cached data" action).
export async function idbClearMeals(uid: string): Promise<void> {
  const dbi = await openDb()
  if (!dbi) return
  return new Promise((resolve) => {
    try {
      const req = dbi.transaction(STORE, 'readwrite').objectStore(STORE).index('uid').openCursor(IDBKeyRange.only(uid))
      req.onsuccess = () => {
        const cursor = req.result
        if (cursor) {
          cursor.delete()
          cursor.continue()
        } else resolve()
      }
      req.onerror = () => resolve()
    } catch {
      resolve()
    } finally {
      dbi.close()
    }
  })
}
